<template>
  <div id="app">
    <!-- Background Decor -->
    <div class="bg-decoration"></div>

    <!-- Header -->
    <header class="site-header">
      <div class="header-inner">
        <div class="logo-container">
          <div class="logo-icon">
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M2 17L12 22L22 17" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M2 12L12 17L22 12" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </div>
          <h1 class="logo">精选导航</h1>
        </div>
        <div class="header-actions">
          <button @click="showReportModal = true" class="action-btn">
            <span class="icon">📢</span> 反馈
          </button>
        </div>
      </div>
      
      <!-- Recent History -->
      <transition name="slide-fade">
        <div class="recent-section" v-if="recentSites.length > 0">
          <div class="recent-header">
            <span class="recent-label">最近常访</span>
            <button @click="clearRecent" class="clear-btn" title="清空记录">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
              </svg>
            </button>
          </div>
          <div class="recent-list-wrapper">
            <div class="recent-list">
              <div 
                v-for="(site, index) in recentSites" 
                :key="'recent-' + index" 
                class="recent-card"
                @click="handleSiteClick(site)"
              >
                <div class="recent-icon-wrapper">
                  <img :src="getIconUrl(site.url)" class="site-icon-img" alt="icon">
                </div>
                <span class="recent-name">{{ site.name }}</span>
              </div>
            </div>
          </div>
        </div>
      </transition>
    </header>

    <!-- Main Content -->
    <main class="main-content">
      <div v-for="(sites, category) in siteData" :key="category" class="category-section">
        <div class="category-header">
          <h2 class="category-title">{{ category }}</h2>
          <div class="category-line"></div>
        </div>
        <div class="site-grid">
          <div 
            v-for="(site, index) in sites" 
            :key="category + '-' + index" 
            class="site-card"
            @click="handleSiteClick(site)"
          >
            <div class="site-card-content">
              <div class="site-icon-container">
                <img :src="getIconUrl(site)" class="site-icon-img" alt="icon" loading="lazy">
              </div>
              <div class="site-info">
                <span class="site-name">{{ site.name }}</span>
                <span class="site-desc" v-if="site.desc">{{ site.desc }}</span>
                <span class="site-desc" v-else>{{ site.url.split('//')[1].split('/')[0] }}</span>
              </div>
            </div>
            <div class="site-hover-effect"></div>
          </div>
        </div>
      </div>
    </main>

    <!-- Footer -->
    <footer class="site-footer">
      <div class="footer-inner">
        <div class="footer-links">
          <a href="#" @click.prevent="showFullDisclaimerModal = true">免责声明</a>
        </div>
        <p class="copyright">© {{ new Date().getFullYear() }} Rain View Navigation. All rights reserved.</p>
      </div>
    </footer>

    <!-- Modals -->
    <transition name="fade">
      <div v-if="showDisclaimer || showFullDisclaimerModal || showReportModal" class="modal-backdrop"></div>
    </transition>

    <!-- Disclaimer Modal -->
    <transition name="modal-pop">
      <div v-if="showDisclaimer" class="modal-wrapper">
        <div class="modal-card">
          <div class="modal-header">
            <h3>即将跳转第三方网站</h3>
          </div>
          <div class="modal-body">
            <div class="warning-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#faad14" stroke-width="1.5">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="8" x2="12" y2="12"></line>
                <line x1="12" y1="16" x2="12.01" y2="16"></line>
              </svg>
            </div>
            <p class="modal-text">您即将离开本站，目标网站内容由第三方提供。请注意信息安全。</p>
            
            <div class="checkbox-group">
              <label class="custom-checkbox">
                <input type="checkbox" v-model="isAgreed">
                <span class="checkmark"></span>
                <span class="label-text">我已阅读并同意 <a href="#" @click.prevent="showFullDisclaimerModal = true">免责条款</a></span>
              </label>
            </div>
          </div>
          <div class="modal-footer">
            <button @click="showDisclaimer = false" class="btn btn-secondary">取消</button>
            <button 
              @click="confirmDisclaimer" 
              class="btn btn-primary" 
              :disabled="!isAgreed"
            >
              继续访问
            </button>
          </div>
        </div>
      </div>
    </transition>

    <!-- Full Disclaimer Modal -->
    <transition name="modal-pop">
      <div v-if="showFullDisclaimerModal" class="modal-wrapper">
        <div class="modal-card modal-lg">
          <div class="modal-header">
            <h3>免责声明</h3>
            <button @click="showFullDisclaimerModal = false" class="close-icon">&times;</button>
          </div>
          <div class="modal-body scrollable">
            <div class="typography">
              <h4>1. 总则</h4>
              <p>欢迎使用“网站导航”（以下简称“本站”）。本站作为浏览器导航页，旨在为用户提供便捷的第三方网站链接入口。在您使用本站服务前，请仔细阅读本免责声明。</p>
              
              <h4>2. 内容说明</h4>
              <p>本站收录的所有网站链接均来自互联网或用户推荐，本站不拥有这些第三方网站的所有权，也不参与其内容的创作、编辑或监控。本站仅提供链接服务，不对第三方网站的内容真实性、合法性、准确性或安全性做任何保证。</p>
              
              <h4>3. 责任限制</h4>
              <p>若用户通过本站访问的第三方网站存在任何违规、侵权、欺诈或传播恶意软件等行为，由此产生的任何法律责任、财产损失或个人隐私泄露等后果，均由该第三方网站及其运营者承担，<strong>本站及您所使用的浏览器软件方不承担任何连带责任。</strong></p>
              
              <h4>4. 使用风险</h4>
              <p>用户应自行判断目标网站的安全性。访问外部链接时产生的风险由用户自行承担。本站建议您在访问陌生网站时保持警惕，并安装必要的安全防护软件。</p>
            </div>
          </div>
          <div class="modal-footer center">
            <button @click="showFullDisclaimerModal = false" class="btn btn-primary">我已了解</button>
          </div>
        </div>
      </div>
    </transition>

    <!-- Report Modal -->
    <transition name="modal-pop">
      <div v-if="showReportModal" class="modal-wrapper">
        <div class="modal-card">
          <div class="modal-header">
            <h3>提交反馈</h3>
          </div>
          <div class="modal-body">
            <div class="form-item">
              <label>反馈类型</label>
              <div class="type-group">
                <button 
                  v-for="type in reportTypes" 
                  :key="type.value"
                  class="type-btn"
                  :class="{ active: reportForm.type === type.value }"
                  @click="reportForm.type = type.value"
                >
                  {{ type.label }}
                </button>
              </div>
            </div>
            <div class="form-item">
              <label>{{ currentTypeConfig.titleLabel }}</label>
              <input v-model="reportForm.title" type="text" class="input-field" :placeholder="currentTypeConfig.placeholder">
            </div>
            <div class="form-item">
              <label>联系邮箱 <span style="color: #ef4444">*</span></label>
              <input v-model="reportForm.email" type="email" class="input-field" placeholder="请填写您的邮箱地址">
            </div>
            <div class="form-item">
              <label>详细内容</label>
              <textarea v-model="reportForm.liyou" class="textarea-field" :placeholder="currentTypeConfig.contentPlaceholder"></textarea>
            </div>
          </div>
          <div class="modal-footer">
            <button @click="showReportModal = false" class="btn btn-secondary" :disabled="isSubmitting">取消</button>
            <button @click="submitReport" class="btn btn-primary" :disabled="isSubmitting">
              <span v-if="isSubmitting" class="spinner"></span>
              <span v-else>提交反馈</span>
            </button>
          </div>
        </div>
      </div>
    </transition>

  </div>
</template>

<script>
import siteData from './assets/siteData.json'

export default {
  name: 'App',
  data() {
    return {
      siteData: siteData,
      recentSites: [],
      showDisclaimer: false,
      isAgreed: false,
      showFullDisclaimerModal: false,
      showReportModal: false,
      pendingUrl: '',
      isSubmitting: false,
      reportForm: {
        type: '申请收录',
        title: '',
        liyou: '',
        email: ''
      },
      reportTypes: [
        { label: '申请收录', value: '申请收录', titleLabel: '网站名称', placeholder: '例如：某某网站', contentPlaceholder: '请提供网站地址及简要描述...' },
        { label: '申请下架', value: '申请下架', titleLabel: '网站名称', placeholder: '例如：某某网站', contentPlaceholder: '请说明下架原因...' },
        { label: '投诉举报', value: '投诉举报', titleLabel: '网站名称', placeholder: '例如：某某网站', contentPlaceholder: '请描述违规情况...' },
        { label: '其他意见', value: '其他意见', titleLabel: '标题', placeholder: '简要概括您的意见', contentPlaceholder: '请详细描述您的建议...' }
      ]
    };
  },
  computed: {
    currentTypeConfig() {
      return this.reportTypes.find(t => t.value === this.reportForm.type) || this.reportTypes[0];
    }
  },
  created() {
    this.loadRecent();
  },
  methods: {
    getIconUrl(site) {
      let url = site.url;
      if(site.icon){
 return site.icon;
      }
      try {
        const base64Url = btoa(url);
        return 'https://icon.rainsee.cn/iconv2.php?base=' + base64Url;
      } catch (e) {
        console.error('Base64 encode failed', e);
        return 'https://icon.rainsee.cn/iconv2.php?base=' + url;
      }
    },
    loadRecent() {
      const stored = localStorage.getItem('recentSites');
      if (stored) {
        try {
          this.recentSites = JSON.parse(stored);
        } catch (e) {
          console.error('Failed to parse recent sites', e);
        }
      }
    },
    addToRecent(site) {
      const index = this.recentSites.findIndex(s => s.url === site.url);
      if (index > -1) {
        this.recentSites.splice(index, 1);
      }
      this.recentSites.unshift(site);
      if (this.recentSites.length > 12) { // Adjusted limit for design
        this.recentSites.pop();
      }
      localStorage.setItem('recentSites', JSON.stringify(this.recentSites));
    },
    clearRecent() {
      this.recentSites = [];
      localStorage.removeItem('recentSites');
    },
    handleSiteClick(site) {
      this.addToRecent(site);
      
      const agreed = localStorage.getItem('agreedDisclaimer');
      if (!agreed) {
        this.pendingUrl = site.url;
        this.isAgreed = false;
        this.showDisclaimer = true;
      } else {
        window.open(site.url, '_blank');
      }
    },
    confirmDisclaimer() {
      localStorage.setItem('agreedDisclaimer', 'true');
      this.showDisclaimer = false;
      if (this.pendingUrl) {
        window.open(this.pendingUrl, '_blank');
        this.pendingUrl = '';
      }
    },
    submitReport() {
      if (!this.reportForm.title || !this.reportForm.liyou || !this.reportForm.email) {
        alert("请填写完整信息（包括邮箱）");
        return;
      }
      
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(this.reportForm.email)) {
        alert("请输入有效的邮箱地址");
        return;
      }
      
      this.isSubmitting = true;
      const formdata = new FormData();
      const combinedContent = `【${this.reportForm.type}】\n联系邮箱：${this.reportForm.email}\n\n${this.reportForm.liyou}`;
      
      formdata.append("liyou", combinedContent);
      formdata.append("title", this.reportForm.title);
      formdata.append("type", this.reportForm.type);
      formdata.append("email", this.reportForm.email);

      const requestOptions = {
        method: 'POST',
        body: formdata,
        redirect: 'follow'
      };

      fetch("https://api.yjllq.com/api/white/set2", requestOptions)
        .then(response => response.text())
        .then(result => {
          console.log(result);
          alert("反馈已提交，感谢您的建议！");
          this.showReportModal = false;
          this.reportForm.title = '';
          this.reportForm.liyou = '';
          this.reportForm.email = '';
          this.reportForm.type = '申请收录';
        })
        .catch(error => {
          console.log('error', error);
          alert("提交失败，请检查网络后重试");
        })
        .finally(() => {
          this.isSubmitting = false;
        });
    }
  }
}
</script>

<style>
:root {
  --primary-color: #3b82f6;
  --primary-hover: #2563eb;
  --bg-color: #f3f4f6;
  --card-bg: #ffffff;
  --text-main: #1f2937;
  --text-secondary: #6b7280;
  --border-color: #e5e7eb;
  --shadow-sm: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
  --shadow-md: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  --shadow-lg: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
  --radius-lg: 16px;
  --radius-md: 12px;
  --radius-sm: 8px;
}

* {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
  -webkit-tap-highlight-color: transparent;
}

body {
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", sans-serif;
  background-color: var(--bg-color);
  color: var(--text-main);
  line-height: 1.5;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

/* Background Decoration */
.bg-decoration {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: 
    radial-gradient(circle at 15% 50%, rgba(59, 130, 246, 0.08), transparent 25%), 
    radial-gradient(circle at 85% 30%, rgba(16, 185, 129, 0.08), transparent 25%);
  z-index: -1;
  pointer-events: none;
}

#app {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

/* Header */
.site-header {
  position: sticky;
  top: 0;
  z-index: 50;
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(12px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.3);
  box-shadow: 0 1px 3px rgba(0,0,0,0.02);
  padding: 0.75rem 0;
}

.header-inner {
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 1.5rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 60px;
}

.logo-container {
  display: flex;
  align-items: center;
  gap: 12px;
}

.logo-icon {
  width: 32px;
  height: 32px;
  color: var(--primary-color);
  background: rgba(59, 130, 246, 0.1);
  padding: 6px;
  border-radius: 8px;
}

.logo {
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--text-main);
  letter-spacing: -0.025em;
}

.action-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: transparent;
  border: 1px solid transparent;
  padding: 0.5rem 1rem;
  border-radius: var(--radius-md);
  color: var(--text-secondary);
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 0.9rem;
}

.action-btn:hover {
  background: rgba(0, 0, 0, 0.03);
  color: var(--text-main);
}

/* Recent Section */
.recent-section {
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 1.5rem 1rem;
}

.recent-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;
  padding: 0 0.25rem;
}

.recent-label {
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--text-secondary);
  font-weight: 600;
}

.clear-btn {
  background: none;
  border: none;
  color: #9ca3af;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  transition: all 0.2s;
}

.clear-btn:hover {
  color: #ef4444;
  background: rgba(239, 68, 68, 0.1);
}

.recent-list-wrapper {
  overflow-x: auto;
  padding: 4px;
  margin: -4px;
  /* Hide scrollbar */
  scrollbar-width: none;
}
.recent-list-wrapper::-webkit-scrollbar {
  display: none;
}

.recent-list {
  display: flex;
  gap: 12px;
}

.recent-card {
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  width: 72px;
  cursor: pointer;
}

.recent-icon-wrapper {
  width: 48px;
  height: 48px;
  background: var(--card-bg);
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: var(--shadow-sm);
  border: 1px solid var(--border-color);
  transition: all 0.2s;
}

.recent-card:hover .recent-icon-wrapper {
  transform: translateY(-2px);
  box-shadow: var(--shadow-md);
  border-color: var(--primary-color);
}

.site-icon-img {
  width: 24px;
  height: 24px;
  object-fit: contain;
}

.recent-name {
  font-size: 0.75rem;
  color: var(--text-secondary);
  text-align: center;
  width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Main Content */
.main-content {
  flex: 1;
  max-width: 1280px;
  margin: 0 auto;
  width: 100%;
  padding: 2rem 1.5rem;
}

.category-section {
  margin-bottom: 3rem;
}

.category-header {
  display: flex;
  align-items: center;
  margin-bottom: 1.5rem;
  gap: 1rem;
}

.category-title {
  font-size: 1.125rem;
  font-weight: 700;
  color: var(--text-main);
  white-space: nowrap;
}

.category-line {
  height: 1px;
  flex: 1;
  background: linear-gradient(to right, var(--border-color), transparent);
}

.site-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 1.25rem;
}

.site-card {
  background: var(--card-bg);
  border-radius: var(--radius-lg);
  padding: 1rem;
  cursor: pointer;
  position: relative;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border: 1px solid rgba(229, 231, 235, 0.5);
  box-shadow: var(--shadow-sm);
  overflow: hidden;
}

.site-card:hover {
  transform: translateY(-4px);
  box-shadow: var(--shadow-lg);
  border-color: rgba(59, 130, 246, 0.3);
}

.site-card-content {
  display: flex;
  align-items: center;
  gap: 12px;
}

.site-icon-container {
  width: 40px;
  height: 40px;
  background: #f9fafb;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.site-info {
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.site-name {
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--text-main);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-bottom: 2px;
}

.site-desc {
  font-size: 0.75rem;
  color: var(--text-secondary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Footer */
.site-footer {
  background: #ffffff;
  border-top: 1px solid var(--border-color);
  padding: 2rem 0;
  margin-top: auto;
}

.footer-inner {
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 1.5rem;
  text-align: center;
}

.footer-links a {
  color: var(--text-secondary);
  text-decoration: none;
  font-size: 0.875rem;
  margin: 0 10px;
  transition: color 0.2s;
}

.footer-links a:hover {
  color: var(--primary-color);
}

.copyright {
  margin-top: 1rem;
  font-size: 0.8rem;
  color: #9ca3af;
}

/* Modal System */
.modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(4px);
  z-index: 1000;
}

.modal-wrapper {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1001;
  pointer-events: none;
}

.modal-card {
  pointer-events: auto;
  background: #ffffff;
  width: 90%;
  max-width: 440px;
  border-radius: 20px;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  display: flex;
  flex-direction: column;
  max-height: 90vh;
  border: 1px solid rgba(255,255,255,0.5);
}

.modal-lg {
  max-width: 640px;
}

.modal-header {
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid var(--border-color);
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-header h3 {
  font-size: 1.125rem;
  font-weight: 700;
  color: var(--text-main);
}

.close-icon {
  background: none;
  border: none;
  font-size: 1.5rem;
  color: #9ca3af;
  cursor: pointer;
  line-height: 1;
}

.modal-body {
  padding: 1.5rem;
  overflow-y: auto;
}

.scrollable {
  max-height: 60vh;
}

.warning-icon {
  display: flex;
  justify-content: center;
  margin-bottom: 1rem;
}

.modal-text {
  text-align: center;
  color: var(--text-secondary);
  margin-bottom: 1.5rem;
  line-height: 1.6;
}

.modal-footer {
  padding: 1.25rem 1.5rem;
  background: #f9fafb;
  border-top: 1px solid var(--border-color);
  border-bottom-left-radius: 20px;
  border-bottom-right-radius: 20px;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

.modal-footer.center {
  justify-content: center;
}

/* Form Styles */
.form-item {
  margin-bottom: 1.25rem;
}

.type-group {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.type-btn {
  padding: 8px 16px;
  border: 1px solid var(--border-color);
  background: #f9fafb;
  color: var(--text-secondary);
  border-radius: var(--radius-md);
  cursor: pointer;
  font-size: 0.9rem;
  transition: all 0.2s;
  outline: none;
}

.type-btn:hover {
  background: #f3f4f6;
  border-color: #d1d5db;
}

.type-btn.active {
  background: rgba(59, 130, 246, 0.1);
  color: var(--primary-color);
  border-color: var(--primary-color);
  font-weight: 600;
}

/* Spinner */
.spinner {
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: #fff;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.form-item label {
  display: block;
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--text-main);
  margin-bottom: 0.5rem;
}

.input-field, .textarea-field {
  width: 100%;
  padding: 0.75rem 1rem;
  border: 1px solid var(--border-color);
  border-radius: var(--radius-md);
  font-size: 0.95rem;
  background: #fff;
  transition: all 0.2s;
  font-family: inherit;
}

.textarea-field {
  height: 120px;
  resize: vertical;
}

.input-field:focus, .textarea-field:focus {
  outline: none;
  border-color: var(--primary-color);
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

/* Buttons */
.btn {
  padding: 0.625rem 1.25rem;
  border-radius: var(--radius-md);
  font-weight: 600;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
}

.btn-primary {
  background: var(--primary-color);
  color: white;
  box-shadow: 0 4px 6px -1px rgba(59, 130, 246, 0.4);
}

.btn-primary:hover:not(:disabled) {
  background: var(--primary-hover);
  transform: translateY(-1px);
  box-shadow: 0 6px 8px -1px rgba(59, 130, 246, 0.5);
}

.btn-primary:disabled {
  background: #9ca3af;
  cursor: not-allowed;
  box-shadow: none;
}

.btn-secondary {
  background: #ffffff;
  border: 1px solid var(--border-color);
  color: var(--text-main);
}

.btn-secondary:hover {
  background: #f9fafb;
  border-color: #d1d5db;
}

/* Checkbox */
.checkbox-group {
  display: flex;
  justify-content: center;
  margin-top: 1rem;
}

.custom-checkbox {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  user-select: none;
  font-size: 0.9rem;
  color: var(--text-secondary);
}

.custom-checkbox input {
  position: absolute;
  opacity: 0;
  cursor: pointer;
  height: 0;
  width: 0;
}

.checkmark {
  height: 20px;
  width: 20px;
  background-color: #fff;
  border: 2px solid #d1d5db;
  border-radius: 6px;
  display: inline-block;
  position: relative;
  transition: all 0.2s;
}

.custom-checkbox:hover input ~ .checkmark {
  border-color: var(--primary-color);
}

.custom-checkbox input:checked ~ .checkmark {
  background-color: var(--primary-color);
  border-color: var(--primary-color);
}

.checkmark:after {
  content: "";
  position: absolute;
  display: none;
  left: 6px;
  top: 2px;
  width: 4px;
  height: 10px;
  border: solid white;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}

.custom-checkbox input:checked ~ .checkmark:after {
  display: block;
}

.custom-checkbox a {
  color: var(--primary-color);
  text-decoration: none;
}

.custom-checkbox a:hover {
  text-decoration: underline;
}

/* Transitions */
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s;
}
.fade-enter, .fade-leave-to {
  opacity: 0;
}

.modal-pop-enter-active, .modal-pop-leave-active {
  transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
}
.modal-pop-enter, .modal-pop-leave-to {
  opacity: 0;
  transform: scale(0.9) translateY(10px);
}

.slide-fade-enter-active {
  transition: all 0.3s ease;
}
.slide-fade-leave-active {
  transition: all 0.3s cubic-bezier(1.0, 0.5, 0.8, 1.0);
}
.slide-fade-enter, .slide-fade-leave-to {
  transform: translateY(-10px);
  opacity: 0;
}

/* Typography for Disclaimer */
.typography h4 {
  margin: 1rem 0 0.5rem;
  color: var(--text-main);
}
.typography p {
  margin-bottom: 0.75rem;
  color: var(--text-secondary);
  font-size: 0.95rem;
  line-height: 1.6;
}

/* Responsive */
@media (max-width: 640px) {
  .header-inner {
    padding: 0 1rem;
  }
  .main-content {
    padding: 1rem 0.75rem;
  }
  .site-grid {
    grid-template-columns: repeat(3, 1fr);
    gap: 0.5rem;
  }
  .site-card {
    padding: 0.5rem;
    border-radius: 10px;
  }
  .site-card-content {
    gap: 6px;
  }
  .site-icon-container {
    width: 28px;
    height: 28px;
    border-radius: 8px;
  }
  .site-icon-img {
    width: 16px;
    height: 16px;
  }
  .site-name {
    font-size: 0.75rem;
    margin-bottom: 0;
    line-height: 1.2;
  }
  .site-desc {
    display: none;
  }
  .modal-card {
    width: 95%;
  }
}
</style>
